import React, { useState, useCallback, useMemo } from 'react';
import {
  IonContent,
  IonSpinner,
  IonText,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  useIonViewWillEnter,
} from '@ionic/react';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';
import {
  cloudDownloadOutline,
  documentTextOutline,
  layersOutline,
  receiptOutline,
  barChartOutline,
  closeCircleOutline,
} from 'ionicons/icons';
import {
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type FilterChangedEvent,
} from 'ag-grid-community';

import ActionGrid from '@/components/common/ActionGrid';
import PageLayout from '@/components/navigation/PageLayout';
import { usePageTitle } from '@/contexts/PageTitleContext';
import lookupDataService from '@/services/data/LookupDataService';
import { config } from '@/config/config';
import AttachmentConfirmModal from '@/components/reporting/AttachmentConfirmModal';
import ExportCsvModal from '@/components/reporting/ExportCsvModal';
import ExportPdfModal from '@/components/reporting/ExportPdfModal';
import GenerateReportModal from '@/components/reporting/GenerateReportModal';
import ChartSeriesModal, { ChartModalConfig } from '@/components/reporting/ChartSeriesModal';
import {
  reportingService,
  ReportDataSource,
  InspectionResultRow,
  InspectionPlanRow,
  LineItemRow,
  CorrectiveActionRow,
  ReportRow,
  ReportAttachment,
} from '@/services/ReportingService';
import {
  ActionGridActionColumn,
  ActionGridBulkAction,
  ActionGridColumnDefinition,
  ActionGridRowModel,
} from '@/types/model/actionGrid';

// ─── Column definitions ───────────────────────────────────────────────────────

const INSPECTION_RESULT_COLS: ColDef<InspectionResultRow>[] = [
  { 
    field: 'lineItem', 
    headerName: 'Line Item', 
    width: 120, 
    filter: true,
  },
  { field: 'title', headerName: 'Title', flex: 2, minWidth: 160, filter: true, tooltipField: 'title' },
  { field: 'group', headerName: 'Group', flex: 2, minWidth: 160, filter: true, tooltipField: 'group' },
  {
    field: 'inspectedOn',
    headerName: 'Inspected On',
    width: 140,
    filter: 'agDateColumnFilter',
    valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : '—'),
  },
  { field: 'siteName', headerName: 'Site', width: 150, filter: true },
  { field: 'inspectorName', headerName: 'Inspector', width: 150, filter: true },
  { field: 'areaOfExpertise', headerName: 'Area of Expertise', flex: 1, minWidth: 140, filter: true },
  {
    field: 'compositeScore',
    headerName: 'Score',
    width: 90,
    filter: 'agNumberColumnFilter',
    valueFormatter: (p) => (p.value != null && p.value >= 0 ? Number(p.value).toFixed(2) : '—'),
  },
  {
    field: 'isExceptional',
    headerName: 'Exceptional',
    width: 110,
    filter: true,
    valueFormatter: (p) => (p.value ? 'Yes' : 'No'),
  },
  { field: 'checklistItemCount', headerName: 'Checklist Items', width: 130, filter: 'agNumberColumnFilter' },
  { field: 'correctiveActionCount', headerName: 'Corrective Actions', width: 150, filter: 'agNumberColumnFilter' },
  { field: 'attachmentCount', headerName: 'Attachments', width: 120, filter: 'agNumberColumnFilter' },
];

const INSPECTION_PLAN_COLS: ColDef<InspectionPlanRow>[] = [
  { 
    field: 'lineItem', 
    headerName: 'Line Item', 
    width: 120, 
    filter: true,
  },
  { field: 'title', headerName: 'Title', flex: 2, minWidth: 160, filter: true, tooltipField: 'title' },
  { field: 'group', headerName: 'Group', width: 160, filter: true },
  {
    field: 'majorRequirement',
    headerName: 'Major Requirement',
    flex: 1,
    minWidth: 160,
    filter: true,
    tooltipField: 'majorRequirement',
  },
  {
    field: 'isActive',
    headerName: 'Active',
    width: 90,
    filter: true,
    valueFormatter: (p) => (p.value ? 'Yes' : 'No'),
    cellStyle: (p) => ({ color: p.value ? '#1a7a1a' : '#888888', fontWeight: '500' }),
  },
  {
    field: 'isLocked',
    headerName: 'Locked',
    width: 90,
    filter: true,
    valueFormatter: (p) => (p.value ? '🔒 Yes' : 'No'),
  },
  { field: 'primaryValue', headerName: 'Primary Value', width: 130, filter: true },
  { field: 'timeToInspect', headerName: 'Hours to Inspect', width: 140, filter: true },
  { field: 'sampleSize', headerName: 'Sample Size', width: 130, filter: true },
  { field: 'checklistItemCount', headerName: 'Checklist Items', width: 130, filter: 'agNumberColumnFilter' },
  { field: 'scheduledInspectionsCount', headerName: 'Scheduled', width: 110, filter: 'agNumberColumnFilter' },
  { field: 'status', headerName: 'Status', width: 120, filter: true },
  {
    field: 'lastUpdated',
    headerName: 'Last Updated',
    width: 130,
    filter: 'agDateColumnFilter',
    valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : '—'),
  },
];

const LINE_ITEM_COLS: ColDef<LineItemRow>[] = [
  { field: 'lineItem', headerName: 'Line Item', width: 120, filter: true },
  { field: 'title', headerName: 'Title', flex: 2, minWidth: 160, filter: true, tooltipField: 'title' },
  { field: 'group', headerName: 'Group', width: 160, filter: true },
  { field: 'groupIdentifier', headerName: 'Group #', width: 90, filter: true },
  {
    field: 'majorRequirement',
    headerName: 'Major Requirement',
    flex: 1,
    minWidth: 160,
    filter: true,
    tooltipField: 'majorRequirement',
  },
  { field: 'relatedInfo', headerName: 'Related Info', flex: 1, minWidth: 140, filter: true, tooltipField: 'relatedInfo' },
  { field: 'performanceStandard', headerName: 'Performance Standard', flex: 1, minWidth: 160, filter: true, tooltipField: 'performanceStandard' },
  { field: 'estimatedQuantity', headerName: 'Est. Quantity', width: 130, filter: true },
  {
    field: 'isActive',
    headerName: 'Active',
    width: 90,
    filter: true,
    valueFormatter: (p) => (p.value ? 'Yes' : 'No'),
    cellStyle: (p) => ({ color: p.value ? '#1a7a1a' : '#888888', fontWeight: '500' }),
  },
  { field: 'modificationStatus', headerName: 'Modification Status', width: 160, filter: true },
  {
    field: 'lastUpdated',
    headerName: 'Last Updated',
    width: 130,
    filter: 'agDateColumnFilter',
    valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : '—'),
  },
  { field: 'attachmentCount', headerName: 'Attachments', width: 120, filter: 'agNumberColumnFilter' },
];

const CORRECTIVE_ACTION_COLS: ColDef<CorrectiveActionRow>[] = [
  { field: 'correctiveActionId', headerName: 'CA #', width: 90, filter: 'agNumberColumnFilter' },
  { field: 'issue', headerName: 'Issue', flex: 2, minWidth: 180, filter: true, tooltipField: 'issue' },
  { field: 'lineItem', headerName: 'Line Item', width: 110, filter: true },
  { field: 'lineItemTitle', headerName: 'Title', flex: 1, minWidth: 140, filter: true, tooltipField: 'lineItemTitle' },
  { field: 'group', headerName: 'Group', width: 150, filter: true },
  { field: 'status', headerName: 'CA Status', width: 120, filter: true },
  { field: 'validationStatus', headerName: 'Validation Status', width: 140, filter: true },
  { field: 'classification', headerName: 'Classification', width: 140, filter: true },
  { field: 'site', headerName: 'Site', width: 130, filter: true },
  {
    field: 'issuedOn',
    headerName: 'Issued On',
    width: 120,
    filter: 'agDateColumnFilter',
    valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : '—'),
  },
  { field: 'issuedBy', headerName: 'Issued By', width: 140, filter: true },
  {
    field: 'acceptedOn',
    headerName: 'Accepted On',
    width: 120,
    filter: 'agDateColumnFilter',
    valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : '—'),
  },
  { field: 'acceptedBy', headerName: 'Accepted By', width: 130, filter: true },
  {
    field: 'lastResponseDate',
    headerName: 'Last Response',
    width: 130,
    filter: 'agDateColumnFilter',
    valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : '—'),
  },
  { field: 'lastResponseBy', headerName: 'Response By', width: 130, filter: true },
  { field: 'lastResponseType', headerName: 'Response Type', width: 130, filter: true },
  { field: 'lastResponseComments', headerName: 'Response Comments', flex: 1, minWidth: 160, filter: true, tooltipField: 'lastResponseComments' },
  { field: 'attachmentCount', headerName: 'Attachments', width: 110, filter: 'agNumberColumnFilter' },
];

// ─── Component ────────────────────────────────────────────────────────────────

const COL_DEFS_MAP: Record<ReportDataSource, ColDef[]> = {
  inspectionResults: INSPECTION_RESULT_COLS as ColDef[],
  inspectionPlans:   INSPECTION_PLAN_COLS   as ColDef[],
  lineItems:         LINE_ITEM_COLS         as ColDef[],
  correctiveActions: CORRECTIVE_ACTION_COLS as ColDef[],
};

const DATA_SOURCE_OPTIONS: { value: ReportDataSource; label: string }[] = [
  { value: 'inspectionPlans', label: 'Inspection Plans' },
  { value: 'lineItems', label: 'Line Items' },
  { value: 'inspectionResults', label: 'Inspection Results' },
  { value: 'correctiveActions', label: 'Corrective Actions' },
];

const ROW_ID_FIELD_BY_SOURCE: Record<ReportDataSource, string> = {
  inspectionResults: 'inspectionResultId',
  inspectionPlans: 'planId',
  lineItems: 'lineItemId',
  correctiveActions: 'correctiveActionId',
};

const getInspectionResultsFromDate = (): string => {
  const days = lookupDataService.getUserPreferences()?.InspectionResultsDays ?? config.Inspecting.pastDaysToShow;
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const getDefaultToDate = (): string => new Date().toISOString().slice(0, 10);

const DEFAULT_CHART_CONFIG: ChartModalConfig = {
  chartType: 'bar',
  xColumn: 'lineItem',
  xColumnLabel: 'Line Item',
  yMetric: 'inspectionCount',
  yMetricLabel: 'Count Of Inspections',
  dateInterval: 'month',
};

const Reporting: React.FC = () => {
  usePageTitle('Reporting');
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const [dataSource, setDataSource] = useState<ReportDataSource>('inspectionPlans');
  const [rowDataBySource, setRowDataBySource] = useState<Record<ReportDataSource, ReportRow[]>>({
    inspectionResults: [],
    inspectionPlans: [],
    lineItems: [],
    correctiveActions: [],
  });
  const [loadingBySource, setLoadingBySource] = useState<Record<ReportDataSource, boolean>>({
    inspectionResults: false,
    inspectionPlans: false,
    lineItems: false,
    correctiveActions: false,
  });
  const [errorBySource, setErrorBySource] = useState<Record<ReportDataSource, string | null>>({
    inspectionResults: null,
    inspectionPlans: null,
    lineItems: null,
    correctiveActions: null,
  });
  const [lookupReady, setLookupReady] = useState(false);

  const [selectedRows, setSelectedRows] = useState<ReportRow[]>([]);
  const [pendingAttachments, setPendingAttachments] = useState<ReportAttachment[]>([]);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  // Modal visibility
  const [attachmentModalOpen, setAttachmentModalOpen] = useState(false);
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [chartConfig, setChartConfig] = useState<ChartModalConfig>(DEFAULT_CHART_CONFIG);

  // Toast
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<ToasterVariant>('success');

  const showMessage = (msg: string, variant: ToasterVariant = 'success') => {
    setToastMessage(msg);
    setToastVariant(variant);
    setShowToast(true);
  };

  const ensureLookupReady = useCallback(async (): Promise<boolean> => {
    try {
      setLookupReady(false);
      await lookupDataService.initialize();
      const ready = lookupDataService.isLoaded();
      setLookupReady(ready);
      return ready;
    } catch (error) {
      console.error('Reporting page lookup initialization failed:', error);
      setLookupReady(false);
      return false;
    }
  }, []);

  // ── Data loading ────────────────────────────────────────────────────────────

  const loadSource = useCallback(async (source: ReportDataSource) => {
    setLoadingBySource((prev) => ({ ...prev, [source]: true }));
    setErrorBySource((prev) => ({ ...prev, [source]: null }));

    try {
      const rows =
        source === 'inspectionResults'
          ? await reportingService.loadInspectionResultRows(getInspectionResultsFromDate(), getDefaultToDate())
          : source === 'lineItems'
            ? await reportingService.loadLineItemRows()
            : source === 'correctiveActions'
              ? await reportingService.loadCorrectiveActionRows()
              : await reportingService.loadInspectionPlanRows();
      setRowDataBySource((prev) => ({ ...prev, [source]: rows }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load data';
      setErrorBySource((prev) => ({ ...prev, [source]: msg }));
      showMessage(`${DATA_SOURCE_OPTIONS.find((opt) => opt.value === source)?.label ?? source}: ${msg}`, 'error');
    } finally {
      setLoadingBySource((prev) => ({ ...prev, [source]: false }));
    }
  }, []);

  const loadAllSources = useCallback(async () => {
    if (!(await ensureLookupReady())) return;

    void loadSource('inspectionPlans');
    void loadSource('lineItems');
    void loadSource('inspectionResults');
    void loadSource('correctiveActions');
  }, [ensureLookupReady, loadSource]);

  useIonViewWillEnter(() => {
    void loadAllSources();
  });

  const handleTabChange = (source: ReportDataSource) => {
    setDataSource(source);
    setSelectedRows([]);
  };

  // ── Grid events ─────────────────────────────────────────────────────────────

  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api);
    setHasActiveFilters(params.api.isAnyFilterPresent());
  }, []);

  const onFilterChanged = useCallback((event: FilterChangedEvent) => {
    setHasActiveFilters(event.api.isAnyFilterPresent());
  }, []);

  // ── Action handlers ─────────────────────────────────────────────────────────

  const handleClearFilters = () => {
    if (!gridApi) return;
    gridApi.setFilterModel(null);
    gridApi.setGridOption('quickFilterText', '');
    setHasActiveFilters(false);
    showMessage('Filters cleared.');
  };

  // ── Derived ─────────────────────────────────────────────────────────────────

  const colDefs = COL_DEFS_MAP[dataSource];
  const rowIdField = ROW_ID_FIELD_BY_SOURCE[dataSource];
  const rowData = rowDataBySource[dataSource];
  const loading = loadingBySource[dataSource];
  const loadError = errorBySource[dataSource];

  const actionGridColumns = useMemo<ActionGridColumnDefinition[]>(() => {
    const mappedColumns = colDefs
      .filter((col): col is ColDef & { field: string } => typeof col.field === 'string')
      .map((col) => {
        const { field, headerName, ...rest } = col;

        return {
          key: field,
          headerName: headerName ?? field,
          isRowId: field === rowIdField,
          colDef: rest as Omit<ColDef<Record<string, unknown>>, 'field' | 'headerName'>,
        };
      });

    if (mappedColumns.some((column) => column.key === rowIdField)) {
      return mappedColumns;
    }

    return [
      {
        key: rowIdField,
        headerName: 'Row ID',
        isRowId: true,
        colDef: {
          hide: true,
          filter: false,
          sortable: false,
        },
      },
      ...mappedColumns,
    ];
  }, [colDefs, rowIdField]);

  const rowLookupById = useMemo(() => {
    const byId = new Map<string, ReportRow>();

    for (const row of rowData) {
      const value = (row as unknown as Record<string, unknown>)[rowIdField];
      if (value != null && value !== '') {
        byId.set(String(value), row);
      }
    }

    return byId;
  }, [rowData, rowIdField]);

  const toReportRows = useCallback((rows: ActionGridRowModel[]): ReportRow[] => {
    const resolvedRows: ReportRow[] = [];

    for (const row of rows) {
      const found = rowLookupById.get(row.rowId);
      if (found) {
        resolvedRows.push(found);
      }
    }

    return resolvedRows;
  }, [rowLookupById]);

  const gridData = useMemo(() => {
    return rowData.map((row) =>
      actionGridColumns.map((column) => (row as unknown as Record<string, unknown>)[column.key])
    );
  }, [actionGridColumns, rowData]);

  const bulkActions = useMemo<ActionGridBulkAction[]>(() => [
    {
      id: 'clear-filters',
      label: 'Clear Filters',
      icon: closeCircleOutline,
      onAction: () => handleClearFilters(),
      isVisible: () => hasActiveFilters,
    },
    {
      id: 'download-attachments',
      label: 'Download Attachments',
      icon: cloudDownloadOutline,
      onAction: async ({ selectedRows: selected }) => {
        const reportRows = toReportRows(selected);
        setSelectedRows(reportRows);
        const attachments = await reportingService.collectAttachments(reportRows);
        setPendingAttachments(attachments);
        setAttachmentModalOpen(true);
      },
      isDisabled: ({ selectedRows: selected }) => selected.length === 0,
    },
    {
      id: 'export-csv',
      label: 'Export to CSV',
      icon: layersOutline,
      onAction: () => {
        setCsvModalOpen(true);
      },
      isDisabled: ({ selectedRows: selected, gridApi: api }) => selected.length === 0 || !api,
    },
    {
      id: 'export-pdf',
      label: 'Export to PDF',
      icon: documentTextOutline,
      onAction: ({ selectedRows: selected }) => {
        setSelectedRows(toReportRows(selected));
        setPdfModalOpen(true);
      },
      isDisabled: ({ selectedRows: selected }) => selected.length === 0,
    },
    {
      id: 'generate-report',
      label: 'Generate Report',
      icon: receiptOutline,
      onAction: ({ selectedRows: selected }) => {
        setSelectedRows(toReportRows(selected));
        setReportModalOpen(true);
      },
      isVisible: () => dataSource === 'inspectionResults',
      isDisabled: ({ selectedRows: selected }) => selected.length === 0,
    },
    {
      id: 'open-chart',
      label: 'Charts',
      icon: barChartOutline,
      onAction: ({ selectedRows: selected }) => {
        setSelectedRows(toReportRows(selected));
        setChartModalOpen(true);
      },
      isVisible: () => dataSource === 'inspectionResults',
      isDisabled: ({ selectedRows: selected }) => selected.length === 0,
    },
  ], [dataSource, hasActiveFilters, toReportRows]);


  const actionColumns = useMemo<ActionGridActionColumn[]>(() => [
    {
      id: 'row-attachments',
      headerName: 'Files',
      width: 84,
      pinned: 'right',
      actions: [
        {
          id: 'download-row-attachments',
          title: 'Download Row Attachments',
          icon: cloudDownloadOutline,
          onAction: async ({ row }) => {
            const reportRow = rowLookupById.get(row.rowId);
            if (!reportRow) return;

            setSelectedRows([reportRow]);
            const attachments = await reportingService.collectAttachments([reportRow]);
            setPendingAttachments(attachments);
            setAttachmentModalOpen(true);
          },
        },
      ],
    },
    {
      id: 'row-reporting',
      headerName: 'Insights',
      width: 84,
      pinned: 'right',
      actions: [
        {
          id: 'row-chart',
          title: 'Chart From Row',
          icon: barChartOutline,
          targetColumnKey: 'lineItem',
          onAction: ({ row }) => {
            const reportRow = rowLookupById.get(row.rowId);
            if (!reportRow) return;

            setSelectedRows([reportRow]);
            setChartModalOpen(true);
          },
          isVisible: () => dataSource === 'inspectionResults',
        },
        {
          id: 'row-generate-report',
          title: 'Generate Report For Row',
          icon: receiptOutline,
          targetColumnKey: 'lineItem',
          onAction: ({ row }) => {
            const reportRow = rowLookupById.get(row.rowId);
            if (!reportRow) return;

            setSelectedRows([reportRow]);
            setReportModalOpen(true);
          },
          isVisible: () => dataSource === 'inspectionResults',
        },
      ],
    },
  ], [dataSource, rowLookupById]);

  const defaultColDef: ColDef = {
    resizable: true,
    sortable: true,
    filter: true,
    minWidth: 80,
  };

  if (!lookupReady) {
    return (
      <PageLayout>
        <IonContent fullscreen className="ion-padding">
          <div style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            color: 'var(--ion-color-medium)',
          }}>
            <IonSpinner name="crescent" />
            <IonText>Loading reporting data...</IonText>
          </div>
        </IonContent>
      </PageLayout>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <PageLayout>

      <IonContent fullscreen className="ion-padding">
        <div style={{ 
          maxWidth: '100%', 
          height: 'calc(100vh - 120px)', 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '12px',
          }}>
          <IonSegment
            value={dataSource}
            onIonChange={(e) => handleTabChange((e.detail.value as ReportDataSource) ?? 'inspectionPlans')}
            style={{ maxWidth: '800px' }}
          >
            {DATA_SOURCE_OPTIONS.map((opt) => (
              <IonSegmentButton key={opt.value} value={opt.value}>
                <IonLabel style={{ lineHeight: '12px', fontSize: 'smaller' }}>{opt.label}</IonLabel>
              </IonSegmentButton>
            ))}
          </IonSegment>

          {loading && rowData.length === 0 ? (
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              color: 'var(--ion-color-medium)',
            }}>
              <IonSpinner name="crescent" />
              <IonText>Loading {DATA_SOURCE_OPTIONS.find((opt) => opt.value === dataSource)?.label}...</IonText>
            </div>
          ) : (
            <>
              {loadError && (
                <IonText color="danger" style={{ fontSize: '0.85rem' }}>
                  ⚠ {loadError}
                </IonText>
              )}

              {/* ── AG-Grid ── */}
              <ActionGrid
                columns={actionGridColumns}
                gridData={gridData}
                bulkActions={bulkActions}
                actionColumns={actionColumns}
                defaultColDef={defaultColDef as ColDef<Record<string, unknown>>}
                onSelectionChanged={(rows) => {
                  setSelectedRows(toReportRows(rows));
                }}
                onGridReady={onGridReady}
                onFilterChanged={onFilterChanged}
                animateRows={true}
                pagination={true}
                paginationPageSize={50}
                paginationPageSizeSelector={[25, 50, 100, 200]}
                overlayNoRowsTemplate='<span style="color:#888;font-size:0.9rem;padding:20px;">No data available for this source.</span>'
              />
            </>
          )}
        </div>

        {/* ── Toast ── */}
        <Toaster
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          variant={toastVariant}
        />

        {/* ── Modals ── */}
        <AttachmentConfirmModal
          isOpen={attachmentModalOpen}
          attachments={pendingAttachments}
          onClose={() => setAttachmentModalOpen(false)}
        />

        <ExportCsvModal
          isOpen={csvModalOpen}
          gridApi={gridApi}
          onClose={() => setCsvModalOpen(false)}
          onSuccess={showMessage}
        />

        <ExportPdfModal
          isOpen={pdfModalOpen}
          selectedRows={selectedRows}
          columnDefs={colDefs}
          onClose={() => setPdfModalOpen(false)}
          onSuccess={showMessage}
        />

        <GenerateReportModal
          isOpen={reportModalOpen}
          selectedRows={selectedRows}
          dataSource={dataSource}
          onClose={() => setReportModalOpen(false)}
        />

        <ChartSeriesModal
          isOpen={chartModalOpen}
          selectedRows={selectedRows}
          config={chartConfig}
          onConfigChange={setChartConfig}
          onClose={() => setChartModalOpen(false)}
        />
      </IonContent>
    </PageLayout>
  );
};

export default Reporting;